import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Button, Checkbox, message } from 'antd';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from 'ckeditor5-custom-bp';
import Loader from '../../Components/Loader/Loader';
import AuthContext from '../../context/auth/authContext';

const PLATFORMS = ['web', 'qbox', 'QoIP', 'stream'];
const LABELS = { web: 'Web', qbox: 'Qbox', QoIP: 'QoIP', stream: 'Stream' };
const MAINTENANCE_ROLES = ['tmc-super-admin', 'tmc-admin', 'sky-maintenance'];

const emptyPlatform = () => ({ enabled: false, text: '' });

// The API may return either the new nested object shape or a legacy
// 'true'/'false' string per platform. Normalize both into the form shape.
const normalize = (value) => {
  if (value && typeof value === 'object') {
    return {
      enabled: value.enabled === true || value.enabled === 'true',
      text: value.text ?? '',
    };
  }
  return { enabled: value === true || value === 'true', text: '' };
};

const MaintenancePage = () => {
  const [data, setData] = useState({
    web: emptyPlatform(),
    qbox: emptyPlatform(),
    QoIP: emptyPlatform(),
    stream: emptyPlatform(),
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const api = useApi();
  const { sendRequest } = api;
  const history = useHistory();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (user && !MAINTENANCE_ROLES.includes(user.role)) {
      history.push('/notAllowed');
    }
  }, [user]);

  const updatePlatform = (platform, patch) =>
    setData((prev) => ({ ...prev, [platform]: { ...prev[platform], ...patch } }));

  const fetchData = async () => {
    const response = await sendRequest(`/pflege/maintenance-page`);
    if (response && response.data.data) {
      const d = response.data.data;
      setData({
        web: normalize(d.web),
        qbox: normalize(d.qbox),
        QoIP: normalize(d.QoIP),
        stream: normalize(d.stream),
      });
    }
    setInitialLoading(false);
  };

  const editorConfig = {
    toolbar: ['bold', 'italic', 'underline', '|', 'link'],
    removePlugins: ['Title'],
  };

  const save = async () => {

    const responseData = await sendRequest(`/pflege/maintenance-page`, {
      method: 'PATCH',
      body: data,
    });

    if (!responseData || responseData.status !== 200) {
      message.error('Something went wrong');
    } else {
      message.success('Maintenance mode updated');
      fetchData();
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (initialLoading || api.isLoading) {
    return <Loader />;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <p>
          To enable maintenance mode for specific platform, check the corresponding checkbox. To disable it, do the
          opposite. When enabled, default text message is:
          <strong>
            <br />
            Zurzeit finden planmäßige Wartungsarbeiten statt.
          </strong>
        </p>
        <p>
          If Maintenance text is entered, it will replace default text message.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: '80vw' }}>
          {PLATFORMS.map((platform) => (
            <div key={platform} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 16, maxWidth: '80vw' }}>
              <Checkbox
                checked={data[platform].enabled}
                onChange={(e) => updatePlatform(platform, { enabled: e.target.checked })}
              >
                <strong>{LABELS[platform]}</strong>
              </Checkbox>
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#555' }}>
                  Maintenance text override:
                </label>
                <CKEditor
                  key={platform}
                  editor={ClassicEditor}
                  config={editorConfig}
                  data={data[platform].text || ''}
                  onChange={(event, editor) => {
                    updatePlatform(platform, { text: editor.getData() });
                  }}
                  onReady={(editor) => {
                    editor.editing.view.change((writer) => {
                      const root = editor.editing.view.document.getRoot();

                      if (root) {
                        writer.setStyle('min-height', '120px', root);
                      }
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <p style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
          <strong>Tip:</strong> QoIP and Stream are essentially the same platform type, but need to be enabled/disabled
          separately. To activate maintenance mode on Stream boxes, QoIP also needs to be enabled.
        </p>
      </div>
      <Button type="primary" onClick={save}>
        Save
      </Button>
    </div>
  );
};

export default MaintenancePage;
